import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataTable, Column } from '../components/DataTable';
import { StatusBadge } from '../components/StatusBadge';
import { mockSuppliers } from '../data/mockData';
import { InfoIcon, UsersIcon, TrendingUpIcon, AwardIcon, AlertTriangleIcon, EyeIcon } from 'lucide-react';
export function SupplierKPIReport() {
  const navigate = useNavigate();
  const activeSuppliers = useMemo(() => mockSuppliers.filter(s => s.status !== 'Pending'), []);
  const metrics = useMemo(() => {
    const total = activeSuppliers.length;
    const avgScore = total > 0 ? Math.round(activeSuppliers.reduce((acc, s) => acc + s.kpiScore, 0) / total) : 0;
    const excellent = activeSuppliers.filter(s => s.kpiScore >= 90).length;
    const atRisk = activeSuppliers.filter(s => s.kpiScore < 60).length;
    return {
      total,
      avgScore,
      excellent,
      atRisk
    };
  }, [activeSuppliers]);
  const columns: Column<any>[] = [{
    key: 'name',
    label: 'Supplier',
    render: s => <div data-id="element-2873">
          <div className="font-medium text-gray-900" data-id="element-2874">{s.name}</div>
          <div className="text-xs text-gray-500" data-id="element-2875">{s.code}</div>
        </div>
  }, {
    key: 'delay',
    label: 'Delivery Delay %',
    render: s => {
      const val = s.deliveryDelayPercent;
      const colorClass = val < 5 ? 'text-emerald-600 bg-emerald-50' : val <= 15 ? 'text-amber-600 bg-amber-50' : 'text-rose-600 bg-rose-50';
      return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colorClass}`} data-id="element-2876">
            {val}%
          </span>;
    }
  }, {
    key: 'rejection',
    label: 'Rejection Rate %',
    render: s => {
      const val = s.rejectionRatePercent;
      const colorClass = val < 2 ? 'text-emerald-600 bg-emerald-50' : val <= 5 ? 'text-amber-600 bg-amber-50' : 'text-rose-600 bg-rose-50';
      return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colorClass}`} data-id="element-2877">
            {val}%
          </span>;
    }
  }, {
    key: 'kpiScore',
    label: 'KPI Score',
    render: s => <span className="font-bold text-gray-900" data-id="element-2878">{s.kpiScore}</span>
  }, {
    key: 'ratingPercentage',
    label: 'Rating %',
    render: s => <div className="flex items-center w-full max-w-[100px]" data-id="element-2879">
          <div className="w-full bg-gray-200 rounded-full h-2 mr-2" data-id="element-2880">
            <div className={`h-2 rounded-full ${s.ratingPercentage >= 90 ? 'bg-emerald-500' : s.ratingPercentage >= 75 ? 'bg-blue-500' : s.ratingPercentage >= 60 ? 'bg-amber-500' : 'bg-rose-500'}`} style={{
          width: `${s.ratingPercentage}%`
        }} data-id="element-2881"></div>
          </div>
          <span className="text-xs font-medium text-gray-600" data-id="element-2882">
            {s.ratingPercentage}%
          </span>
        </div>
  }, {
    key: 'performanceStatus',
    label: 'Performance',
    render: s => <StatusBadge status={s.performanceStatus} data-id="element-2883" />
  }, {
    key: 'actions',
    label: 'Action',
    render: s => <button onClick={() => navigate(`/supplier-kpi/${s.id}`)} className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors" data-id="element-2884">
          <EyeIcon className="h-3.5 w-3.5 mr-1" data-id="element-2885" />
          View
        </button>
  }];
  return <div className="space-y-6 h-full flex flex-col pb-8" data-id="element-2886">
      <div className="flex items-center justify-between" data-id="element-2887">
        <div data-id="element-2888">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-2889">
            Supplier KPI Report
          </h1>
          <p className="text-sm text-gray-500 mt-1" data-id="element-2890">
            Analyze supplier performance based on delivery and quality metrics.
          </p>
        </div>
      </div>

      {/* Formula Card */}
      <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-4 flex items-start space-x-3" data-id="element-2891">
        <InfoIcon className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" data-id="element-2892" />
        <div data-id="element-2893">
          <h3 className="text-sm font-semibold text-indigo-900" data-id="element-2894">
            KPI Calculation Methodology
          </h3>
          <div className="mt-1 text-sm text-indigo-800 space-y-1" data-id="element-2895">
            <p data-id="element-2896">
              <strong data-id="element-2897">Delivery Score</strong> = max(0, 100 - (Delivery Delay % ×
              5))
            </p>
            <p data-id="element-2898">
              <strong data-id="element-2899">Quality Score</strong> = max(0, 100 - (Rejection Rate % ×
              10))
            </p>
            <p data-id="element-2900">
              <strong data-id="element-2901">Final KPI Score</strong> = (Delivery Score × 0.6) +
              (Quality Score × 0.4)
            </p>
            <p className="text-xs mt-2 opacity-80" data-id="element-2902">
              Rating Scale: ≥90 Excellent | ≥75 Good | ≥60 Average | &lt;60 Poor
            </p>
          </div>
        </div>
      </div>

      {/* Summary Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-2903">
        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm flex items-center space-x-4" data-id="element-2904">
          <div className="p-3 bg-gray-50 rounded-lg text-gray-600" data-id="element-2905">
            <UsersIcon className="w-6 h-6" data-id="element-2906" />
          </div>
          <div data-id="element-2907">
            <p className="text-sm font-medium text-gray-500" data-id="element-2908">Total Suppliers</p>
            <p className="text-2xl font-bold text-gray-900" data-id="element-2909">{metrics.total}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm flex items-center space-x-4" data-id="element-2910">
          <div className="p-3 bg-blue-50 rounded-lg text-blue-600" data-id="element-2911">
            <TrendingUpIcon className="w-6 h-6" data-id="element-2912" />
          </div>
          <div data-id="element-2913">
            <p className="text-sm font-medium text-gray-500" data-id="element-2914">
              Average KPI Score
            </p>
            <p className="text-2xl font-bold text-gray-900" data-id="element-2915">
              {metrics.avgScore}
            </p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm flex items-center space-x-4" data-id="element-2916">
          <div className="p-3 bg-emerald-50 rounded-lg text-emerald-600" data-id="element-2917">
            <AwardIcon className="w-6 h-6" data-id="element-2918" />
          </div>
          <div data-id="element-2919">
            <p className="text-sm font-medium text-gray-500" data-id="element-2920">
              Excellent Performers
            </p>
            <p className="text-2xl font-bold text-gray-900" data-id="element-2921">
              {metrics.excellent}
            </p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm flex items-center space-x-4" data-id="element-2922">
          <div className="p-3 bg-rose-50 rounded-lg text-rose-600" data-id="element-2923">
            <AlertTriangleIcon className="w-6 h-6" data-id="element-2924" />
          </div>
          <div data-id="element-2925">
            <p className="text-sm font-medium text-gray-500" data-id="element-2926">
              At Risk (&lt;60)
            </p>
            <p className="text-2xl font-bold text-gray-900" data-id="element-2927">{metrics.atRisk}</p>
          </div>
        </div>
      </div>

      {/* Data Table */}
      <div className="flex-1 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden flex flex-col" data-id="element-2928">
        <div className="p-4 border-b border-gray-200 bg-gray-50" data-id="element-2929">
          <h2 className="text-lg font-medium text-gray-900" data-id="element-2930">
            Supplier Performance Details
          </h2>
        </div>
        <div className="flex-1 p-0" data-id="element-2931">
          <DataTable columns={columns} data={activeSuppliers} keyExtractor={item => item.id} searchPlaceholder="Search suppliers..." data-id="element-2932" />
        </div>
      </div>
    </div>;
}